import {
  cardStyle,
  Chips,
  column,
  Css,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  GridTableApi,
  multiFilter,
  MultiSelectField,
  RowStyles,
  selectColumn,
  simpleHeader,
  singleFilter,
  Tag,
  useComputed,
  useFilter,
  useSnackbar,
} from "@homebound/beam";
import { Maybe } from "graphql/jsutils/Maybe";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { SearchBox, useNavigationCheck } from "src/components";
import { ArchivedTag } from "src/components/ArchivedTag";
import { useGlobalOptionTagsFilter } from "src/components/autoPopulateFilters/useGlobalOptionTagsFilter";
import {
  LocationsQuery,
  LocationsSelectField_LocationFragment,
  useLocationsQuery,
} from "src/components/autoPopulateSelects/LocationsSelectField.generated";
import { useGlobalOptionTypes } from "src/components/autoPopulateSelects/useGlobalOptionTypes";
import { NextStepButton } from "src/components/stepper/useStepperWizard/NextStepButton";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { GlobalOptionsFilter, GlobalOptionStatus } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  ChooseOptionsStepBodyV1_GlobalOptionFragment,
  ChooseOptionsStepBodyV1_ReadyPlanOptionFragment,
  ChooseOptionsStepBodyV1Query,
  useChooseOptionsStepBodyV1_SaveReadyPlanChosenOptionsMutation,
  useChooseOptionsStepBodyV1Query,
} from "src/routes/developments/plan-and-options/options/components/ChooseOptionsStepBodyV1.generated";
import { TableActions } from "src/routes/layout/TableActions";
import { queryResult } from "src/utils";

type ChooseOptionsStepBodyV1Props = {
  planId: string;
  returnUrl?: string;
  /** Function to set the step as dirty or not dirty */
  setStepDirty: (dirty: boolean) => void;
  tableApi: GridTableApi<ChooseOptionsRowV1>;
};

export function ChooseOptionsStepBodyV1(props: ChooseOptionsStepBodyV1Props) {
  const { planId, setStepDirty, returnUrl, tableApi } = props;
  const { triggerNotice } = useSnackbar();

  // Get the global option types to use in our filter
  const gots = useGlobalOptionTypes({ version: [1] });
  // This sublist of option types is used to limit the filter options, and the global options to query for when no filter is set
  const optionTypes = useMemo(() => gots?.filter((got) => got.globalOptionsCount > 0) ?? [], [gots]);
  const { data: locationsData } = useLocationsQuery({ fetchPolicy: "cache-and-network" });
  const tagsFilter = useGlobalOptionTagsFilter();

  // Build the filter
  const filterDefs: FilterDefs<ChooseOptionsFilter> = useMemo(
    () => ({
      type: multiFilter({
        label: "Type",
        options: optionTypes,
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ id }) => id,
      }),
      locations: multiFilter({
        label: "Location",
        options: locationsData?.locations ?? [],
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ id }) => id,
      }),
      tags: tagsFilter,
      sortBy: singleFilter({
        label: "Sort By",
        options: [
          { name: "Recently Edited", value: "updatedAt" as const },
          { name: "Recently Created", value: "createdAt" as const },
          { name: "Option Code", value: "code" as const },
        ],
        nothingSelectedText: "Recently Used First",
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ value }) => value,
      }),
    }),
    [optionTypes, locationsData, tagsFilter],
  );
  const { filter, setFilter } = useFilter({ filterDefs });

  const query = useChooseOptionsStepBodyV1Query({
    variables: {
      readyPlanId: planId,
      filter: {
        ...mapToFilter(filter, optionTypes),
        status: [GlobalOptionStatus.Active],
      },
    },
    // No point on running the query if option types have not been loaded yet
    skip: optionTypes.isEmpty,
  });
  const [saveRpos] = useChooseOptionsStepBodyV1_SaveReadyPlanChosenOptionsMutation();
  const disabled = disableBasedOnPotentialOperation(query.data?.readyPlanLike2.canEdit);

  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const selectedRows = useComputed(() => tableApi.getSelectedRows("globalOption"), [tableApi]);

  // get from query, but also set locally
  const [rows, setRows] = useState<GridDataRow<ChooseOptionsRowV1>[]>([]);
  useEffect(() => {
    if (query.data) setRows(createRows(query.data, filter.sortBy ?? "lastUsedAt"));
  }, [query.data, filter.sortBy]);

  const existingRposByKey = useMemo(
    () =>
      new Map<string, ChooseOptionsStepBodyV1_ReadyPlanOptionFragment>(
        query.data?.readyPlanLike2.options.map((rpo) => [keyRpo(rpo), rpo]),
      ),
    [query.data],
  );

  const mapSelectionToSaveRpoInputs = useCallback(() => {
    const desiredRposByKey = new Map<string, { globalOptionId: string; locationId: string | undefined }>(
      selectedRows.flatMap((row) =>
        row.data.locations.map((loc) => [keyRpo(row.data.id, loc), { globalOptionId: row.data.id, locationId: loc }]),
      ),
    );

    // create RPOs that are not in the existing RPOs
    const toCreate = [...desiredRposByKey.entries()]
      .filter(([key]) => !existingRposByKey.has(key))
      .map(([_, value]) => ({ readyPlanId: planId, ...value }));

    // enable/disable RPOs that are in the existing RPOs but not in the desired RPOs
    const toEnableDisable =
      [...existingRposByKey.values()]
        // we first filter only for the relevant option types, to prevent disabling options not displayed on this step
        .filter((rpo) => optionTypes.some((goType) => goType.id === rpo.type.id))
        // then we filter for the ones where there desired status does not match there current active state
        .filter((rpo) => desiredRposByKey.has(keyRpo(rpo)) !== rpo.active)
        // and toggle active for the existing options
        .map((rpo) => ({ id: rpo.id, active: !rpo.active })) ?? [];

    return [...toCreate, ...toEnableDisable];
  }, [existingRposByKey, optionTypes, planId, selectedRows]);

  const onSave = useCallback(async () => {
    const saveRpoInputs = mapSelectionToSaveRpoInputs();
    if (saveRpoInputs.nonEmpty) {
      await saveRpos({ variables: { input: { readyPlanOptions: saveRpoInputs } } });
      triggerNotice({ message: `Your Options have been updated!` });
    }
  }, [mapSelectionToSaveRpoInputs, saveRpos, triggerNotice]);

  const onClickSave = useCallback(async () => {
    await onSave();
  }, [onSave]);

  const saveRpoInputs = mapSelectionToSaveRpoInputs();
  const changeCount = saveRpoInputs.length;
  useEffect(() => {
    setStepDirty(changeCount > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeCount]);

  // if there are no active locations when user selects a row
  // activate the 'None' location
  useEffect(() => {
    selectedRows.forEach((row) => {
      if (row.data.locations.isEmpty) {
        row.data.locations.push(undefined);
        setRows([...rows]);
      }
    });
  }, [selectedRows, rows]);
  const { useRegisterNavigationCheck } = useNavigationCheck();
  useRegisterNavigationCheck(() => changeCount > 0, [changeCount]);

  return (
    <>
      {queryResult(query, () => (
        <div css={Css.pt3.pb0.bgGray100.df.fdc.fg1.$}>
          <TableActions>
            <div css={Css.df.gap1.jcfe.$}>
              <Filters filter={filter} filterDefs={filterDefs} onChange={setFilter} />
            </div>
            <SearchBox onSearch={setSearchFilter} clearable updateQueryString={false} />
          </TableActions>

          <div css={Css.fg1.pb1.$}>
            <GridTable
              api={tableApi}
              rows={rows}
              columns={createColumns(locationsData, rows, setRows, tableApi, existingRposByKey, disabled)}
              filter={searchFilter}
              style={tableStyles}
              rowStyles={rowStyles}
              as="virtual"
            />
          </div>
        </div>
      ))}
      <NextStepButton
        label="Continue"
        onClick={onClickSave}
        onCloseReturnUrl={returnUrl}
        exitButton={{ variant: "secondary", label: "Save & Exit", onClick: onClickSave }}
      />
    </>
  );
}

type ChooseOptionsFilter = GlobalOptionsFilter & {
  sortBy: undefined | "lastUsedAt" | "createdAt" | "updatedAt" | "code";
};

function createColumns(
  locationsData: LocationsQuery | undefined,
  rows: GridDataRow<ChooseOptionsRowV1>[],
  setRows: React.Dispatch<React.SetStateAction<GridDataRow<ChooseOptionsRowV1>[]>>,
  api: GridTableApi<ChooseOptionsRowV1>,
  existingRposByKey: Map<string, ChooseOptionsStepBodyV1_ReadyPlanOptionFragment>,
  disabled?: ReactNode,
): GridColumn<ChooseOptionsRowV1>[] {
  return [
    ...(disabled
      ? [
          column<ChooseOptionsRowV1>({
            header: "",
            globalOption: ({ id, locations }) => ({
              content: (
                <input
                  type="checkbox"
                  disabled
                  checked={locations.some(
                    (loc) =>
                      !existingRposByKey.has(keyRpo(id, loc)) ||
                      existingRposByKey.get(keyRpo(id, loc))?.active !== false,
                  )}
                />
              ),
              value: id,
            }),
            w: "45px",
          }),
        ]
      : [selectColumn<ChooseOptionsRowV1>()]),
    column<ChooseOptionsRowV1>({
      header: "Option Code",
      globalOption: ({ code }) => ({ content: () => code, css: Css.xs.$ }),
      w: "150px",
    }),
    column<ChooseOptionsRowV1>({
      header: "Name",
      globalOption: ({ name }) => ({ content: name, css: Css.sm.$ }),
      w: 3,
    }),
    column<ChooseOptionsRowV1>({
      header: "Type",
      globalOption: ({ type }) => ({ content: <Tag text={type.name} />, css: Css.sm.$, value: type.name }),
    }),
    column<ChooseOptionsRowV1>({
      header: "Location",
      globalOption: (data, opts) => (
        <MultiSelectField<LocationsSelectField_LocationFragment, string | undefined>
          disabled={
            disabled || (!data.type.allowsLocations && `Options of type ${data.type.name} do not allow locations`)
          }
          label="Locations"
          options={[{ name: "None", id: undefined } as any, ...(locationsData?.locations ?? [])]}
          values={data.locations}
          getOptionMenuLabel={(loc) => {
            if (!loc.id) return loc.name;

            return (
              <ArchivedTag
                active={
                  !existingRposByKey.has(keyRpo(data.id, loc.id)) ||
                  existingRposByKey.get(keyRpo(data.id, loc.id))?.active !== false
                }
              >
                <span>
                  <Tag xss={Css.m0.$} type={"info"} text={loc.type.name} /> {loc.name}{" "}
                  {loc.name !== loc.displayLocationPath && <span css={Css.gray500.$}>{loc.displayLocationPath}</span>}
                </span>
              </ArchivedTag>
            );
          }} // add archived tag if rpo exists for this location but is inactive
          getOptionValue={(loc) => loc.id}
          onSelect={(selectedLocations) => {
            // set locations in the row
            const locations = selectedLocations.map((id) => id);

            // get parent and child that matches this row
            // so we can set data in the rows state
            const rowIndex = rows.findIndex((r) => r.id === opts.row.id);
            if (rowIndex !== undefined && rows[rowIndex]) {
              // need to keep existing, but archived, locations
              rows[rowIndex].data = { ...data, locations };
              setRows([...rows]);

              // set selected if there are locations selected (including "None" option)
              // deselected if zero are selected
              api.selectRow(opts.row.id, selectedLocations.length > 0);
            }
          }}
        />
      ),
    }),
    column<ChooseOptionsRowV1>({
      header: "Tag",
      globalOption: (data) => ({
        content: () => (data.tags.length ? <Chips values={data.tags.map((t) => t.name)} wrap compact /> : undefined),
        value: data.tags.map((t) => `${t.id} ${t.name}`).join(" "),
      }),
    }),
  ];
}

/** Create rows of "GO + Location" pairs. */
function createRows(
  data: ChooseOptionsStepBodyV1Query,
  sortBy: "lastUsedAt" | "createdAt" | "updatedAt" | "code",
): GridDataRow<ChooseOptionsRowV1>[] {
  // Create a set of the ReadyPlan's already-selected
  // In theory we could `keyBy` instead of `groupBy`, but there is legacy RPOs that are duplicated
  const existingRpos = data.readyPlanLike2.options.groupBy((rpo) => rpo.globalOption.id) ?? {};
  // Group by option type
  const [initiallySelected, others] = data.globalOptions
    .map(
      (go) =>
        ({
          kind: "globalOption" as const,
          id: go.id,
          data: {
            ...go,
            readyPlanOptions: existingRpos[go.id],
            locations: existingRpos[go.id]?.filter((rpo) => rpo.active).map((rpo) => rpo.location?.id) ?? [],
          },
          initSelected: !!existingRpos[go.id]?.some((rpo) => rpo.active),
        }) as GridDataRow<GlobalOptionRowV1>,
    )
    .partition((c) => c.initSelected);

  function sortOptions(options: GridDataRow<GlobalOptionRowV1>[]): GridDataRow<GlobalOptionRowV1>[] {
    // For lastUsedAt, which may be null, use a date before HB was founded so that they sort to the end
    const gosSorted = options.sortBy((c) => c.data[sortBy] ?? new Date(2000, 0, 1));
    if (sortBy === "code") return gosSorted;
    // For the dates, we want the earliest first, which is the reverse of the standard sort
    return gosSorted.reverse();
  }

  return [simpleHeader, ...sortOptions(initiallySelected), ...sortOptions(others)];
}

function mapToFilter(filterWithSort: ChooseOptionsFilter, globalOptionTypes: NamedFragment[]): GlobalOptionsFilter {
  const { sortBy, ...filter } = filterWithSort;
  // If `type` filter is empty, return only the globalOptionTypes that we want to show (i.e. Elevation may or may not be included)
  const type = filter.type && filter.type.length > 0 ? filter.type.compact() : globalOptionTypes.map((got) => got.id);
  return { ...filter, type };
}

const tableStyles = {
  ...cardStyle,
  cellCss: {
    ...cardStyle.cellCss,
    ...Css.aic.pPx(12).$,
  },
  presentationSettings: {
    borderless: false,
    typeScale: "xs" as const,
    wrap: false,
  },
};

const rowStyles: RowStyles<ChooseOptionsRowV1> = {
  header: {
    rowCss: Css.bgGray100.bn.important.$,
    cellCss: Css.bgGray100.bn.important.$,
  },
  globalOption: {
    rowCss: Css.bgWhite.bn.boxShadow("0px 4px 8px 0px #35353514").mt1.borderRadius("8px").hPx(44).$,
    cellCss: Css.bgWhite.$,
  },
};

// Helper method for comparing desired and existing RPOs
function keyRpo(globalOptionId: string, locationId: string | undefined): string;
function keyRpo(rpo: ChooseOptionsStepBodyV1_ReadyPlanOptionFragment): string;
function keyRpo(
  rpoOrGlobalOptionId: string | ChooseOptionsStepBodyV1_ReadyPlanOptionFragment,
  locationId?: string | undefined,
): string {
  if (typeof rpoOrGlobalOptionId === "string") return `${rpoOrGlobalOptionId}-${locationId}`;
  return `${rpoOrGlobalOptionId.globalOption.id}-${rpoOrGlobalOptionId.location?.id}`;
}

type HeaderRowV1 = {
  kind: "header";
  id: string;
  data: undefined;
};
export type GlobalOptionRowV1 = {
  kind: "globalOption";
  id: string | null;
  data: ChooseOptionsStepBodyV1_GlobalOptionFragment & {
    // Include the existing RPO, even if it's inactive
    readyPlanOptions: Maybe<ChooseOptionsStepBodyV1_ReadyPlanOptionFragment>[];
    locations: Array<string | undefined>;
  };
  initSelected: boolean;
};

export type ChooseOptionsRowV1 = HeaderRowV1 | GlobalOptionRowV1;
